/**
 * Who is signed in to this window, and the token every engine request carries
 * (owner, 2026-10-29: accounts and roles, engine side in nova/auth).
 *
 * With accounts off the engine says so and nothing here changes anything: every
 * screen behaves as before and `may()` allows all.
 *
 * - **One wrapper for every request.** `installSessionFetch` wraps `fetch` once:
 *   a request to the engine gets `Authorization: Bearer <token>`, and a 401 that
 *   asks for a sign-in shows the sign-in screen. So no call site needs to know.
 * - **Media cannot send headers** (audio, pictures, a plugin's script):
 *   `withToken(url)` puts the token in the address instead.
 * - **"Stay signed in on this computer"** keeps the token in localStorage (the
 *   engine then keeps the session 30 days); otherwise sessionStorage, gone with
 *   the tab, and the engine ends it after 12 hours unused.
 */

import { useSyncExternalStore } from "react";

export interface SessionUser {
  id: number;
  username: string;
  displayName: string;
  role: string;
  roleId: number;
  isAdmin: boolean;
  permissions: string[];
}

export interface SessionState {
  /** Asked the engine yet. */
  known: boolean;
  /** Accounts are on. */
  enabled: boolean;
  user: SessionUser | null;
  /** The engine asked this window to sign in (accounts on, no valid token). */
  needsSignIn: boolean;
  /** This window signed out on purpose (not a session that ran out). */
  signedOut?: boolean;
  /** The last time this window asked, the engine answered (false: it could not be reached). */
  reachable?: boolean;
}

const KEY = "nova.session";
let state: SessionState = { known: false, enabled: false, user: null, needsSignIn: false };
const listeners = new Set<() => void>();
let base = "";

function set(next: Partial<SessionState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function read(store: "local" | "session"): string | null {
  try {
    return (store === "local" ? localStorage : sessionStorage).getItem(KEY);
  } catch {
    return null;
  }
}

/** The token this window signs its requests with, if any. */
export function sessionToken(): string | null {
  return read("session") ?? read("local");
}

function keep(token: string | null, remember: boolean) {
  try {
    localStorage.removeItem(KEY);
    sessionStorage.removeItem(KEY);
    if (token) (remember ? localStorage : sessionStorage).setItem(KEY, token);
  } catch {
    // Private window: the token lives only as long as this page.
  }
  memory = token;
}
let memory: string | null = null;

function token(): string | null {
  return sessionToken() ?? memory;
}
/** The token in force (also one kept only in memory, in a private window). */
export const currentToken = (): string | null => token();

/** Called when the session changes (signed in or out); returns the stop function. */
export function onSessionChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** `url` with the token in it, for things that cannot send headers. Unchanged with no token. */
export function withToken(url: string): string {
  const t = token();
  if (!t) return url;
  return `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(t)}`;
}

function isEngine(url: string): boolean {
  if (url.startsWith("/api/") || url.startsWith("/plugins/")) return true;
  return !!base && url.startsWith(base);
}

let installed = false;

/** Wrap `fetch` so every engine request carries the token. `engine` is the engine's address. */
export function installSessionFetch(engine: string) {
  base = engine.replace(/\/$/, "");
  if (installed || typeof window === "undefined") return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const t = token();
    if (!isEngine(url) || !t) return original(input, init);
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${t}`);
    const res = await original(input, { ...init, headers });
    if (res.status === 401 && !url.includes("/api/auth/")) set({ needsSignIn: true, user: null });
    return res;
  };
}

/** Ask the engine whether accounts are on and who this window's token is. */
export async function refreshSession(engine: string): Promise<SessionState> {
  installSessionFetch(engine);
  try {
    const res = await fetch(`${base}/api/auth/status`);
    const body = (await res.json()) as { enabled: boolean; user: SessionUser | null };
    set({ known: true, reachable: true, enabled: !!body.enabled, user: body.user, needsSignIn: !!body.enabled && !body.user });
  } catch {
    set({ known: true, reachable: false });
  }
  return state;
}

async function post<T>(path: string, body: unknown): Promise<T & { error?: string }> {
  const res = await fetch(`${base}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return (await res.json()) as T & { error?: string };
}

/** Sign in. Returns an error to show, or null. */
export async function signIn(username: string, password: string, remember: boolean): Promise<string | null> {
  const r = await post<{ token: string; user: SessionUser }>("/api/auth/login", {
    username, password, remember, client: navigator.userAgent,
  }).catch(() => ({ error: "Nova's engine cannot be reached." }) as { error: string; token?: string; user?: SessionUser });
  if (r.error || !r.token || !r.user) return r.error ?? "Could not sign in.";
  keep(r.token, remember);
  set({ user: r.user, needsSignIn: false, enabled: true, signedOut: false });
  return null;
}

/** Turn accounts on with this person as the first Administrator (only while they are off). */
export async function enableAccounts(username: string, password: string, displayName: string): Promise<string | null> {
  const r = await post<{ token: string; user: SessionUser }>("/api/auth/enable", {
    username, password, displayName, remember: true,
  });
  if (r.error || !r.token || !r.user) return r.error ?? "Could not turn accounts on.";
  keep(r.token, true);
  set({ user: r.user, enabled: true, needsSignIn: false });
  return null;
}

export async function signOut() {
  await post("/api/auth/logout", {}).catch(() => undefined);
  keep(null, false);
  set({ user: null, needsSignIn: state.enabled, signedOut: true });
}

/** After accounts were turned off. */
export function accountsOff() {
  keep(null, false);
  set({ enabled: false, user: null, needsSignIn: false });
}

export function getSession(): SessionState {
  return state;
}

export function useSession(): SessionState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state,
  );
}

/** Whether this window may do what `permission` covers: always, with accounts off. */
export function may(permission: string, s: SessionState = state): boolean {
  if (!s.enabled) return true;
  if (!s.user) return false;
  return s.user.isAdmin || permission === "signed_in" || s.user.permissions.includes(permission);
}

/** `may`, kept current as the session changes. */
export function useMay(): (permission: string) => boolean {
  const s = useSession();
  return (p: string) => may(p, s);
}
